/**
 * Pillar Descriptions
 * 
 * Detailed explanations of what each readiness pillar measures and why it matters.
 * Used to provide context to users via info tooltips.
 */

export interface PillarDescription {
  id: string;
  name: string;
  shortDescription: string;
  scope: string;
  whyItMatters: string;
  whatWeMeasure: string[];
}

export const PILLAR_DESCRIPTIONS: Record<string, PillarDescription> = {
  technical_hygiene: {
    id: 'technical_hygiene',
    name: 'On-Page Technical SEO',
    shortDescription: 'Evaluates the technical foundations that enable search engines to crawl, understand, and index your website effectively.',
    scope: 'Analyzes crawlability, indexability, URL configuration, server responses, and essential technical SEO elements that affect search engine access.',
    whyItMatters: 'Strong technical SEO helps search engines discover, crawl, and index your content efficiently. Technical issues can prevent pages from appearing in search results, reduce crawl efficiency, and negatively impact overall website visibility.',
    whatWeMeasure: [
      'HTTP status codes (2xx, 3xx, 4xx, and 5xx)',
      'Robots.txt configuration',
      'Meta robots directives',
      'XML sitemap availability and accessibility',
      'Canonical URL implementation',
      'Redirect chains and redirect loops',
      'HTTPS implementation and security',
      'URL crawlability and indexability',
      'Duplicate URL detection',
      'Overall Site Technical SEO health score'
    ]
  },
  
  indexability: {
    id: 'indexability',
    name: 'Google Index Coverage',
    shortDescription: 'Measures how many of your pages are successfully indexed by Google using Search Console data.',
    scope: 'Analyzes Google indexing status, coverage, exclusions, and indexing health based on Google Search Console reports.',
    whyItMatters: 'Only pages indexed by Google are eligible to appear in Google Search results. Monitoring index coverage helps identify pages that require attention before they can rank.',
    whatWeMeasure: [
      'Indexed pages',
      'Excluded and non-indexed pages',
      'Pages with indexing errors or warnings',
      'Index coverage ratio',
      'Submitted vs. indexed sitemap URLs',
      'Last crawl and indexing status',
      'Recently indexed pages',
      'Coverage trends',
      'Indexing reasons and exclusions',
      'Overall indexing health score'
    ]
  },
  
  internal_linking: {
    id: 'internal_linking',
    name: 'Internal Link Graph',
    shortDescription: 'Evaluates your website\'s internal linking structure to improve crawlability, navigation, and SEO performance.',
    scope: 'Analyzes how pages are connected through internal links, including link distribution, orphan pages, broken links, and overall site architecture.',
    whyItMatters: 'A strong internal linking structure helps search engines discover and understand your content, distributes page authority throughout the site, improves user navigation, and ensures important pages can be crawled and indexed efficiently.',
    whatWeMeasure: [
      'Pages with incoming internal links',
      'Pages with outgoing internal links',
      'Orphan pages with no inbound internal links',
      'Broken internal links pointing to 4xx or 5xx pages',
      'Internal link distribution across the website',
      'Link depth from the homepage',
      'Overlinked and underlinked pages',
      'Internal anchor text usage',
      'Links to redirecting URLs',
      'Overall internal linking health score'
    ]
  },
  
  performance: {
    id: 'performance',
    name: 'Core Web Vitals Performance',
    shortDescription: 'Evaluates your website\'s speed, responsiveness, visual stability, and overall user experience using Google\'s Core Web Vitals and PageSpeed metrics.',
    scope: 'Analyzes Core Web Vitals, server response time, rendering performance, and PageSpeed Insights scores across mobile and desktop devices.',
    whyItMatters: 'Fast, responsive websites provide a better user experience, improve engagement and conversions, and support stronger search visibility. Google uses Core Web Vitals as ranking signals to evaluate page experience.',
    whatWeMeasure: [
      'Largest Contentful Paint (LCP)',
      'Interaction to Next Paint (INP)',
      'Cumulative Layout Shift (CLS)',
      'Time to First Byte (TTFB)',
      'First Contentful Paint (FCP)',
      'Speed Index',
      'Total Blocking Time (TBT)',
      'Mobile PageSpeed performance score',
      'Desktop PageSpeed performance score',
      'Overall Core Web Vitals assessment'
    ]
  },
  
  content_intent: {
    id: 'content_intent',
    name: 'Content Quality & Search Intent',
    shortDescription: 'Evaluates your content\'s quality, relevance, and alignment with user search intent to improve search visibility and engagement.',
    scope: 'Analyzes content quality, topical relevance, search intent alignment, readability, structure, metadata, and overall content optimization.',
    whyItMatters: 'High-quality content that satisfies user intent is more likely to rank well, engage visitors, earn backlinks, and drive meaningful conversions. Search engines prioritize content that is relevant, comprehensive, trustworthy, and helpful.',
    whatWeMeasure: [
      'Search intent alignment',
      'Content depth and comprehensiveness',
      'Topic coverage and semantic relevance',
      'Title tag and meta description optimization',
      'Heading structure (H1–H6)',
      'Content readability and formatting',
      'Keyword usage and topical relevance',
      'Image and multimedia usage',
      'Content freshness and update signals',
      'Duplicate and thin content detection',
      'Call-to-action (CTA) presence',
      'Overall content quality score'
    ]
  },

  ai_searchability: {
    id: 'ai_searchability',
    name: 'AI Searchability',
    shortDescription: 'Evaluates how discoverable, understandable, and citable your content is for AI-powered search engines and generative AI assistants.',
    scope: 'Analyzes AI crawler accessibility, structured content, semantic markup, entity signals, and content readiness for AI search, retrieval, and citation.',
    whyItMatters: 'AI-powered search and answer engines increasingly rely on high-quality, well-structured, and accessible content. Optimizing for AI search improves the likelihood that your content is discovered, understood, and cited in AI-generated responses.',
    whatWeMeasure: [
      'AI crawler accessibility (GPTBot, ClaudeBot, PerplexityBot, and other AI crawlers)',
      'LLM discovery file (llms.txt) availability and validity',
      'Semantic HTML structure for AI content extraction',
      'Structured data and entity schema (Organization, Article, FAQ, Breadcrumb, etc.)',
      'Author, publication, and freshness signals',
      'Content chunking and heading hierarchy',
      'Citation readiness and source attribution signals',
      'AI-friendly metadata and canonical URLs',
      'Machine-readable content quality',
      'Overall AI search readiness score'
    ]
  }
};

/**
 * Get pillar description by ID
 */
export const getPillarDescription = (pillarId: string): PillarDescription | undefined => {
  return PILLAR_DESCRIPTIONS[pillarId];
};

/**
 * Get all pillar descriptions in display order
 */
export const getAllPillarDescriptions = (): PillarDescription[] => {
  const order = ['technical_hygiene', 'indexability', 'internal_linking', 'performance', 'content_intent'];
  return order.map(id => PILLAR_DESCRIPTIONS[id]).filter(Boolean);
};
